import { useEffect, useMemo } from "react";
import { FaArrowDown, FaArrowUp, FaBullseye, FaChartPie, FaPiggyBank, FaReceipt, FaSyncAlt, FaWallet } from "react-icons/fa";
import { SAVINGS_CONSTANTS } from "../../_Savings/_MODULES/_API";
import { MONTHLY_FINANCE_CONSTANTS } from "../../_MonthlyExpenses/_MODULES/_API";
import { PageHeader } from "../../../../../_CORE/APP/Templates/_UI/PageHeader/_index";
import { AppButton } from "../../../../../_CORE/APP/Templates/_UI/Button/_index";
import { MetricCard, MetricGrid } from "../../../../../_CORE/APP/Templates/_UI/Metrics/_index";
import { useApiQuery } from "../../../../../_CORE/APP/Hooks/Hooks/_Records/_useApiQuery";
import { ViewLayout } from "../../../../_UI/ViewLayout";
import "../_UI/_Styles/WorkspaceDashboard.css";

const isCurrentMonth = (value) => {
    if (!value) return false;
    const date = new Date(value);
    const now = new Date();
    return date.getFullYear() === now.getFullYear() && date.getMonth() === now.getMonth();
};

export const FinanceDashboardViewMain = () => {
    const accountsQuery = useApiQuery({ minTimeQuery: 160, enablePagination: false, startLoading: true });
    const transactionsQuery = useApiQuery({ minTimeQuery: 160, enablePagination: false, startLoading: true });
    const goalsQuery = useApiQuery({ minTimeQuery: 160, enablePagination: false, startLoading: true });
    const savingsBudgetsQuery = useApiQuery({ minTimeQuery: 160, enablePagination: false, startLoading: true });
    const monthlyEntriesQuery = useApiQuery({ minTimeQuery: 160, enablePagination: false, startLoading: true });
    const recurringQuery = useApiQuery({ minTimeQuery: 160, enablePagination: false, startLoading: true });
    const monthlyBudgetsQuery = useApiQuery({ minTimeQuery: 160, enablePagination: false, startLoading: true });

    useEffect(() => {
        accountsQuery.apiGet({ endpoint: SAVINGS_CONSTANTS.ACCOUNT_LIST });
        transactionsQuery.apiGet({ endpoint: SAVINGS_CONSTANTS.TRANSACTION_LIST });
        goalsQuery.apiGet({ endpoint: SAVINGS_CONSTANTS.GOAL_LIST });
        savingsBudgetsQuery.apiGet({ endpoint: SAVINGS_CONSTANTS.BUDGET_LIST });
        monthlyEntriesQuery.apiGet({ endpoint: MONTHLY_FINANCE_CONSTANTS.ENTRY_LIST });
        recurringQuery.apiGet({ endpoint: MONTHLY_FINANCE_CONSTANTS.RECURRING_LIST });
        monthlyBudgetsQuery.apiGet({ endpoint: MONTHLY_FINANCE_CONSTANTS.BUDGET_LIST });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const accounts = Array.isArray(accountsQuery.useApiData) ? accountsQuery.useApiData : [];
    const transactions = Array.isArray(transactionsQuery.useApiData) ? transactionsQuery.useApiData : [];
    const goals = Array.isArray(goalsQuery.useApiData) ? goalsQuery.useApiData : [];
    const savingsBudgets = Array.isArray(savingsBudgetsQuery.useApiData) ? savingsBudgetsQuery.useApiData : [];
    const monthlyEntries = Array.isArray(monthlyEntriesQuery.useApiData) ? monthlyEntriesQuery.useApiData : [];
    const recurring = Array.isArray(recurringQuery.useApiData) ? recurringQuery.useApiData : [];
    const monthlyBudgets = Array.isArray(monthlyBudgetsQuery.useApiData) ? monthlyBudgetsQuery.useApiData : [];

    const monthEntries = useMemo(() => monthlyEntries.filter((entry) => isCurrentMonth(entry.occurredAt)), [monthlyEntries]);
    const monthIncomeCount = monthEntries.filter((entry) => entry.type === "INCOME").length;
    const monthExpenseCount = monthEntries.filter((entry) => entry.type === "EXPENSE").length;
    const activeGoals = goals.filter((goal) => !["COMPLETED", "CANCELLED"].includes(goal.status)).length;
    const activeRecurring = recurring.filter((item) => item.isActive !== false).length;
    const currencies = new Set(accounts.map((account) => account.currency).filter(Boolean));
    const isLoading = [accountsQuery, transactionsQuery, goalsQuery, savingsBudgetsQuery, monthlyEntriesQuery, recurringQuery, monthlyBudgetsQuery]
        .some((query) => query.useApiLoading);

    return (
        <ViewLayout
            header={
                <PageHeader
                    title="Finanzas"
                    description="Resumen de cuentas, ahorro, metas, movimientos, presupuestos y gastos mensuales sin mezclar monedas."
                    icon={<FaWallet />}
                    actions={
                        <div className="LStudio__workspace_dashboard_actions">
                            <AppButton to="/savings/summary" variant="outline" tone="neutral" icon={<FaPiggyBank />}>Ahorrador</AppButton>
                            <AppButton to="/monthly-expenses/summary" icon={<FaReceipt />}>Gastos mensuales</AppButton>
                        </div>
                    }
                />
            }
        >
            <section className="LStudio__workspace_dashboard_section">
                <div className="LStudio__workspace_dashboard_heading">
                    <span>Panel financiero</span>
                    <small>Indicadores por cantidad para evitar sumar importes de monedas diferentes.</small>
                </div>

                <MetricGrid columns="auto">
                    <MetricCard label="Cuentas" value={accounts.length} icon={<FaWallet />} tone="accent" isLoading={isLoading} to="/savings/accounts" footer={`${currencies.size} monedas en uso`} />
                    <MetricCard label="Movimientos" value={transactions.length} icon={<FaSyncAlt />} tone="neutral" isLoading={isLoading} to="/savings/transactions" />
                    <MetricCard label="Metas activas" value={activeGoals} icon={<FaBullseye />} tone="accent" isLoading={isLoading} to="/savings/goals" />
                    <MetricCard label="Ingresos del mes" value={monthIncomeCount} icon={<FaArrowUp />} tone="ok" isLoading={isLoading} to="/monthly-expenses/entries" />
                    <MetricCard label="Gastos del mes" value={monthExpenseCount} icon={<FaArrowDown />} tone="warn" isLoading={isLoading} to="/monthly-expenses/entries" />
                    <MetricCard label="Recurrentes activos" value={activeRecurring} icon={<FaReceipt />} tone="neutral" isLoading={isLoading} to="/monthly-expenses/recurring" />
                    <MetricCard label="Presupuestos" value={savingsBudgets.length + monthlyBudgets.length} icon={<FaChartPie />} tone="neutral" isLoading={isLoading} footer="Ahorrador + gastos mensuales" />
                </MetricGrid>
            </section>
        </ViewLayout>
    );
};
